<template>
  <q-card flat style="width: 720px; min-width: 0; max-width: calc(100vw - 48px); max-height: 80vh">
    <q-card-section>
      <div class="text-h6 row items-center">Bulk Import</div>
      <div class="q-mt-xs text-grey-7">
        Take advantage of our bulk upload feature to quickly import multiple properties from a CSV
        file and save valuable time.
      </div>
    </q-card-section>
    <q-separator />
    <q-expansion-item
      id="bulk-upload-template_info"
      content-class="overflow-hidden"
      header-class="q-px-md q-py-md"
    >
      <template #header>
        <q-item-section>
          <div class="text-weight-medium">CSV Template</div>
          <div class="text-grey-7">
            Download the CSV template and fill in the required columns to bulk import your
            properties.
          </div>
          <div class="text-primary q-mt-xs">Expand for required formatting info</div>
        </q-item-section>
        <q-item-section side>
          <q-btn
            id="bulk-upload-template_download"
            :href="sampleCSVURL"
            download="hellodata-bulk-upload-template.csv"
            label="Download"
            no-caps
            outline
            unelevated
            @click.stop
          />
        </q-item-section>
      </template>
      <q-card-section
        id="bulk-upload-template_details"
        class="q-pt-none text-grey-8"
        style="max-height: 240px; overflow-x: hidden; overflow-y: auto; overflow-wrap: break-word"
      >
        <div class="text-weight-medium q-mb-xs">Header row</div>
        <p>
          Your CSV must include all of these columns, even if you leave their cells blank:
          <br />
          <span v-for="(col, idx) in templateColumns" :key="col">
            <code>{{ col }}</code
            >{{ idx < templateColumns.length - 1 ? ', ' : '' }}
          </span>
        </p>
        <p>Extra columns (like <code>notes</code>) are ignored.</p>

        <div class="text-weight-medium q-mb-xs">Required in every row</div>
        <ul class="q-mt-none">
          <li>
            <code>query</code>: the property name or street address. Rows without it are skipped.
            Light typos are fine.
          </li>
          <li>
            <code>type</code>: <code>subject</code> or <code>comparable</code>. Rows with any other
            value are skipped.
          </li>
        </ul>

        <div class="text-weight-medium q-mb-xs">Grouping rows into reports</div>
        <p>
          Rows with the same <code>group_id</code> become one report, and each group needs exactly
          one <code>subject</code>. Groups with no subject or more than one are skipped. Comparable
          rows are optional; if a group has none, we'll recommend comparables for you.
        </p>

        <div class="text-weight-medium q-mb-xs">Optional (leave blank if unknown)</div>
        <ul class="q-mt-none q-mb-none">
          <li><code>group_id</code>: if blank, the row becomes its own report.</li>
          <li>
            <code>zip_code</code>: improves match accuracy. Use 5 digits (eg: 60601). 4-digit and
            ZIP+4 values are converted; anything else is ignored.
          </li>
          <li>
            <code>state</code>: improves match accuracy. Use a 2-letter code (eg: CA); anything else
            is ignored.
          </li>
          <li>
            <code>lat</code> and <code>lon</code>: used only when both are filled in. Limits matches
            to within 0.5 miles of that point.
          </li>
          <li>
            <code>tags</code>: comma-separated. Each tag must already exist in your organization;
            unknown tags are dropped. Reports without tags are tagged <code>Active</code>.
          </li>
          <li>
            <code>report_name</code>: defaults to the subject's building name or street address.
          </li>
          <li>
            <code>company_wide</code>: <code>TRUE</code> shares the report with your whole
            organization. Otherwise, only you can see it.
          </li>
        </ul>
      </q-card-section>
    </q-expansion-item>
    <q-separator />
    <q-card-section class="row justify-between items-center">
      <div class="q-mb-sm">
        <div class="text-weight-medium">Upload Data CSV</div>
        <div class="text-grey-7">Upload the Data CSV file to import addresses.</div>
      </div>
      <q-file
        v-model="file"
        accept=".csv"
        color="primary"
        dense
        :disable="uploadStarted"
        label="Select a CSV file"
        outlined
        style="min-width: 300px"
        @update:model-value="uploaded"
      />
    </q-card-section>
    <q-separator />
    <template v-if="errors.length">
      <q-card-section>
        <div class="text-weight-medium">Errors</div>
        <ul
          class="text-negative"
          style="max-height: 100px; overflow-y: auto; margin-right: -16px; padding-right: 16px"
        >
          <li v-for="err in errors" :key="err">{{ err }}</li>
        </ul>
      </q-card-section>
      <q-separator />
    </template>
    <template v-if="importErrors.length">
      <q-card-section id="bulk-upload-import-errors">
        <div class="text-weight-medium">Reports Not Created</div>
        <div class="text-grey-7 q-mb-xs">
          Fix these rows and upload a CSV containing only them to retry. Reports that were created
          successfully are already on your dashboard.
        </div>
        <ul
          class="text-negative"
          style="max-height: 150px; overflow-y: auto; margin-right: -16px; padding-right: 16px"
        >
          <li v-for="err in importErrors" :key="err">{{ err }}</li>
        </ul>
      </q-card-section>
      <q-separator />
    </template>
    <q-card-section v-if="rows.length > 0" class="scroll" style="max-height: 400px">
      <div class="text-weight-medium q-mb-md">Confirm Selection</div>
      <div v-if="rows.length > 0" class="q-gutter-y-sm">
        <q-virtual-scroll v-slot="{ item }" :item-size="10" :items="rows">
          <q-card bordered class="q-mb-md" flat>
            <div v-for="(p, pIdx) in [item.subject].concat(item.comps)" :key="pIdx">
              <div class="row justify-between items-center">
                <div class="q-pa-sm">
                  {{ p.query }}
                  <span v-if="pIdx === 0">(Subject)</span>
                  <span v-else>(Comparable {{ pIdx }})</span>
                </div>
                <div v-if="p.tags?.length && pIdx === 0">
                  <q-badge
                    v-for="tag in p.tags"
                    :key="tag"
                    class="q-mr-sm cursor-pointer"
                    color="accent"
                    rounded
                  >
                    {{ tag }}
                  </q-badge>
                </div>
              </div>
              <q-separator />
              <!-- Let users pick the right response (or none) -->
              <div class="row q-gutter-col-md q-pa-sm">
                <q-radio
                  v-for="prop in foundProperties[p.query]"
                  :key="prop.id"
                  v-model="selectedProperties[p.query]"
                  :val="prop.id"
                  class="col-6 q-mb-sm"
                  color="green"
                  dense
                >
                  <div v-if="prop.building_name" class="text-weight-medium">
                    {{ prop.building_name }}
                  </div>
                  {{ prop.street_address }}
                  <div class="text-grey-7">
                    {{ prop.city }}, {{ prop.state }} {{ prop.zip_code }}
                  </div>
                  <div
                    v-if="prop.number_units || prop.year_built"
                    class="text-grey-7 q-gutter-x-sm"
                  >
                    <span v-if="prop.number_units" class="text-grey-7"
                      >Units: {{ prop.number_units }}</span
                    >
                    <span v-if="prop.year_built" class="text-grey-7"
                      >Year Built: {{ prop.year_built }}</span
                    >
                  </div>
                </q-radio>
                <q-radio
                  v-model="selectedProperties[p.query]"
                  :val="null"
                  class="col-6"
                  color="grey"
                  dense
                  label="Do Not Import"
                />
              </div>
            </div>
            <q-separator v-if="item.comps.length === 0" />
            <div v-if="item.comps.length === 0" class="q-pa-sm">
              <q-icon class="q-mr-sm" color="accent" name="info" />
              <span class="q-pa-sm text-grey-7">We'll recommend comps for you</span>
            </div>
          </q-card>
        </q-virtual-scroll>
      </div>
    </q-card-section>
    <q-separator />
    <q-linear-progress
      v-if="loadingPropertiesProgress"
      :value="loadingPropertiesProgress"
      color="accent"
    />
    <q-linear-progress
      v-if="generatingReportsProgress || uploadStarted"
      :value="generatingReportsProgress || 0"
      color="primary"
    />
    <q-card-actions align="right">
      <q-btn v-close-popup label="Close" no-caps unelevated />
      <q-btn
        v-if="validImports"
        :label="`Generate ${validImports} Reports`"
        :loading="
          searchingProperties ||
          (loadingPropertiesProgress !== null && loadingPropertiesProgress > 0) ||
          (generatingReportsProgress !== null && generatingReportsProgress > 0) ||
          uploadStarted
        "
        :disable="searchingProperties || uploadStarted"
        color="primary"
        no-caps
        unelevated
        @click="generateReports"
      />
    </q-card-actions>
  </q-card>
</template>

<script lang="ts">
import Papa from 'papaparse'
import { chunkedMapPromiseAll, OrganizationVisibility, type PropertySearchResponse } from 'common'
import { usePropertyStore } from '@/stores/property'
import { usePropertyReportStore } from '@/stores/propertyreport'
import { useUserStore } from '@/stores/user'
import { compResponseToSearch } from '@/utils'

interface CSVRow {
  idx: number
  group_id: string | undefined
  type: 'subject' | 'comparable'
  query: string
  zip_code?: string
  state?: string
  tags?: string[]
  lat?: number
  lon?: number
  report_name?: string
  company_wide?: boolean
}

const TEMPLATE_COLUMNS = [
  'group_id',
  'type',
  'query',
  'zip_code',
  'state',
  'lat',
  'lon',
  'tags',
  'report_name',
  'company_wide'
] as const satisfies readonly (keyof CSVRow)[]

const csvLineNumber = (dataIndex: number): number => dataIndex + 2

const validifyZipCode = (zip: string): string | null => {
  // takes the given string, and returns a 5-digit zip code if possible (append 0 if 4 digits, or remove if ZIP+4 format)
  // use regex
  if (zip.match(/^\d{5}$/)) {
    return zip
  } else if (zip.match(/^\d{4}$/)) {
    return '0' + zip
  } else if (zip.match(/^\d{5}-\d{4}$/)) {
    return zip.split('-')[0]
  }
  return null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const describeApiError = (error: unknown): string => {
  if (!isRecord(error)) {
    return 'Unknown error'
  }
  const response = isRecord(error.response) ? error.response : null
  const body = isRecord(error.body)
    ? error.body
    : response && isRecord(response.data)
      ? response.data
      : null

  if (body) {
    const message = typeof body.message === 'string' ? body.message : null
    const details = isRecord(body.details)
      ? Object.values(body.details)
          .map((detail) => (isRecord(detail) ? detail.message : null))
          .filter((detail): detail is string => typeof detail === 'string')
      : []
    if (message && details.length) {
      return `${message}: ${details.join('; ')}`
    }
    if (message) {
      return message
    }
  }

  return typeof error.message === 'string' && error.message ? error.message : 'Unknown error'
}

export interface BulkImportResult {
  succeeded: number
  failed: number
}

export default {
  name: 'BulkCsvImport',
  components: {},
  emits: {
    finished: (result: BulkImportResult) => typeof result.succeeded === 'number'
  },
  data() {
    return {
      file: null as File | null,
      reports: {} as { [key: string]: CSVRow[] },
      errors: [] as string[],
      importErrors: [] as string[],
      templateColumns: TEMPLATE_COLUMNS,
      foundProperties: {} as { [key: string]: PropertySearchResponse[] },
      selectedProperties: {} as { [key: string]: string | null },
      searchingProperties: false,
      completedReports: 0,
      sampleCSVURL:
        'https://storage.googleapis.com/hello-data-ai-public-assets/hellodata-bulk-upload-template.csv',
      uploadStarted: false
    }
  },
  computed: {
    countProperties(): number {
      // count the number of unique properties
      const queries = Object.values(this.reports)
        .flat()
        .map((row: CSVRow) => row.query)
      return new Set(queries).size
    },
    countFoundProperties(): number {
      return Object.keys(this.foundProperties).length
    },
    loadingPropertiesProgress(): number | null {
      if (
        this.countProperties === 0 ||
        this.countFoundProperties === 0 ||
        this.countProperties === this.countFoundProperties
      ) {
        return null
      }
      return this.countFoundProperties / this.countProperties
    },
    generatingReportsProgress(): number | null {
      return this.rows.length > 0 ? this.completedReports / this.rows.length : null
    },
    validImports(): number {
      // count the number of reports that have a valid subject with a key in the selectedProperties
      return Object.keys(this.reports).reduce((acc, key) => {
        const subject = this.reports[key].find((row) => row.type === 'subject')
        if (subject === undefined) {
          return acc
        }

        return acc + (typeof this.selectedProperties[subject.query] === 'string' ? 1 : 0)
      }, 0)
    },
    rows(): { key: string; subject: CSVRow; comps: CSVRow[] }[] {
      const values: { key: string; subject: CSVRow; comps: CSVRow[] }[] = []

      for (const key in this.reports) {
        const subject = this.reports[key].find((row) => row.type === 'subject')
        if (subject === undefined) {
          console.warn(`the row with key ${key} does not have a subject`)
          continue
        }

        const comps = this.reports[key].filter((row) => row.type === 'comparable')
        values.push({ key, subject, comps })
      }

      return values
    },
    availableReportTags() {
      return useUserStore().availableReportTags
    }
  },
  methods: {
    validateAndFilterTags(tags: string[], rowIndex: number): string[] {
      const existingTags = this.availableReportTags
      const validTags: string[] = []
      const invalidTags: string[] = []

      tags.forEach((tag) => {
        if (existingTags.includes(tag)) {
          validTags.push(tag)
        } else {
          invalidTags.push(tag)
        }
      })

      if (invalidTags.length > 0) {
        this.errors.push(
          `Row ${rowIndex}: The following tags do not exist in your organization: ${invalidTags.join(', ')}. Only existing tags will be used. You can ask an admin to add them in the organization settings.`
        )
      }

      return validTags
    },
    selectedPropertyFor(
      row: CSVRow,
      selectedProperties: { [key: string]: string | null },
      foundProperties: { [key: string]: PropertySearchResponse[] }
    ): PropertySearchResponse | undefined {
      const selectedID = selectedProperties[row.query]
      if (typeof selectedID !== 'string') {
        return undefined
      }
      return foundProperties[row.query]?.find((prop) => prop.id === selectedID)
    },
    async generateReport(
      subject: CSVRow,
      comps: CSVRow[],
      selectedProperties: { [key: string]: string | null },
      foundProperties: { [key: string]: PropertySearchResponse[] }
    ): Promise<'created' | 'skipped'> {
      if (typeof selectedProperties[subject.query] !== 'string') {
        return 'skipped'
      }

      const selectedProperty = this.selectedPropertyFor(
        subject,
        selectedProperties,
        foundProperties
      )
      if (selectedProperty === undefined) {
        throw new Error('the selected subject property could not be found. Re-upload the CSV.')
      }

      let comparableAddresses = comps.flatMap(
        (comp) => this.selectedPropertyFor(comp, selectedProperties, foundProperties) ?? []
      )
      let name =
        subject.report_name || selectedProperty.building_name || selectedProperty.street_address

      if (comparableAddresses.length === 0) {
        // we look for comps ourselves
        const retrievedSubject = await usePropertyStore().details(selectedProperty.id)
        const resp = await usePropertyStore().comparables(retrievedSubject, {
          excludedIds: [],
          selectedIds: [],
          topN: 10
        })
        if (resp.comparables.length === 0) {
          throw new Error(
            'no comparables were found for this property. Add comparable rows to this group and try again.'
          )
        }
        comparableAddresses = resp.comparables.map(compResponseToSearch)
        name =
          subject.report_name || retrievedSubject.building_name || retrievedSubject.street_address
      }

      await usePropertyReportStore().create(
        {
          name,
          subject: selectedProperty,
          comparable_addresses: comparableAddresses,
          excluded_property_ids: [],
          is_organization_visible: subject.company_wide ?? false,
          organization_visibility: subject.company_wide ? OrganizationVisibility.EDITOR : null,
          tags: subject.tags?.length ? subject.tags : ['Active'],
          view_preferences: [],
          allow_nonowner_development_edits: false
        },
        { throwOnError: true }
      )
      return 'created'
    },
    async generateReports() {
      // restart the processing state
      this.completedReports = 0
      this.uploadStarted = true
      this.importErrors = []

      const result: BulkImportResult = { succeeded: 0, failed: 0 }
      const rows = this.rows.slice()
      const selectedProperties = { ...this.selectedProperties }
      const foundProperties = { ...this.foundProperties }

      for (const { subject, comps } of rows) {
        try {
          const outcome = await this.generateReport(
            subject,
            comps,
            selectedProperties,
            foundProperties
          )
          if (outcome === 'created') {
            result.succeeded += 1
          }
        } catch (e) {
          console.error(e)
          result.failed += 1
          const label = subject.report_name || subject.query
          this.importErrors.push(
            `Row ${subject.idx}: "${label}" was not created: ${describeApiError(e)}`
          )
        } finally {
          this.completedReports += 1
        }
      }

      this.$tracker.bulkImportedCSV(result.succeeded)
      this.$emit('finished', result)

      this.completedReports = 0
      this.uploadStarted = false
      this.reports = {}
      this.foundProperties = {}
      this.selectedProperties = {}
      this.file = null
    },
    async findProperties() {
      const store = usePropertyStore()

      const retrieveData = (row: CSVRow) => {
        if (this.foundProperties[row.query]) {
          return Promise.resolve()
        }

        let lat: number | undefined
        let lon: number | undefined
        let maxDistance: number | undefined
        if (row.lat !== undefined && row.lon !== undefined) {
          lat = row.lat
          lon = row.lon
          maxDistance = 0.5
        }

        return store
          .propertySearch(
            row.query,
            { lat, lon, zipCode: row.zip_code, state: row.state, maxDistance: maxDistance },
            false
          )
          .then((foundProps) => {
            // take top 3 results
            this.foundProperties[row.query] = foundProps.slice(0, 3)

            // select the first result by default
            if (foundProps.length > 0) {
              this.selectedProperties[row.query] = foundProps[0].id
            } else {
              this.selectedProperties[row.query] = null
              this.errors.push(
                `Row ${row.idx}: No matching property was found for "${row.query}". It will not be imported. Check the spelling or add a zip_code or state.`
              )
            }
          })
          .catch((e: unknown) => {
            this.foundProperties[row.query] = []
            this.selectedProperties[row.query] = null
            this.errors.push(
              `Row ${row.idx}: Could not search for "${row.query}": ${describeApiError(e)}`
            )
          })
      }

      this.searchingProperties = true
      try {
        await chunkedMapPromiseAll(Object.values(this.reports).flat(), 20, retrieveData)
      } finally {
        this.searchingProperties = false
      }
    },
    uploaded() {
      if (this.uploadStarted) {
        return
      }

      // reset the state
      this.reports = {}
      this.foundProperties = {}
      this.selectedProperties = {}
      this.errors = []
      this.importErrors = []

      // read the address and zip_code columns from the csv file
      if (!this.file) {
        return
      }

      Papa.parse(this.file, {
        header: true,
        skipEmptyLines: true,
        complete: (results: {
          data: Record<string, unknown>[]
          errors: { row: number; message: string }[]
        }) => {
          this.errors = results.errors.map((err) => `Row ${err.row}: ${err.message}`)
          if (results.data.length === 0) {
            this.errors.push('CSV file is empty')
            return
          }

          for (const col of TEMPLATE_COLUMNS) {
            if (results.data[0][col] === undefined) {
              this.errors.push(`CSV file must have column: ${col}`)
              return
            }
          }

          const reports: { [key: string]: CSVRow[] } = {}
          let autoGroupID = 0

          for (let i = 0; i < results.data.length; i++) {
            const row = results.data[i] as unknown as CSVRow // we have verified that the required columns exist before
            if (!row.query) {
              this.errors.push(`Row ${csvLineNumber(i)}: query is required`)
              continue
            }
            const parsedZip = validifyZipCode(row.zip_code || '')
            if (parsedZip !== null) {
              row.zip_code = parsedZip
            } else {
              if (row.zip_code) {
                this.errors.push(
                  `Row ${csvLineNumber(i)}: zip_code must be 5 digits (eg: 60601 or 00601). Not using zip_code ${row.zip_code}`
                )
              }
              row.zip_code = undefined
            }
            if (row.state && row.state.length !== 2) {
              this.errors.push(
                `Row ${csvLineNumber(i)}: state must be 2 characters (eg: CA). Not using state ${row.state}`
              )
              row.state = undefined
            }
            row.type = row.type.toLowerCase()
            if (row.type !== 'subject' && row.type !== 'comparable') {
              this.errors.push(
                `Row ${csvLineNumber(i)}: type must be 'subject' or 'comparable'. Not using type ${String(row.type)}`
              )
              continue
            }
            if (!row.group_id) {
              row.group_id = 'autogroup-' + autoGroupID.toString()
              autoGroupID += 1
            }
            if (!reports[row.group_id]) {
              reports[row.group_id] = []
            }

            let rawTags = []
            if (Array.isArray(row.tags)) {
              rawTags = row.tags.filter((tag) => tag && tag.trim() !== '')
            } else if (typeof row.tags === 'string') {
              rawTags = (row.tags as string)
                .split(',')
                .map((tag) => tag.trim())
                .filter((tag) => tag !== '')
            }

            // Validate tags and filter out any that don't exist in the organization
            const validTags = this.validateAndFilterTags(rawTags, csvLineNumber(i))

            const companyWide =
              typeof row.company_wide === 'string'
                ? (row.company_wide as string).toLowerCase() === 'true'
                : Boolean(row.company_wide)

            reports[row.group_id].push({
              idx: csvLineNumber(i),
              group_id: row.group_id,
              type: row.type,
              query: row.query,
              zip_code: row.zip_code,
              state: row.state ? row.state : undefined,
              tags: validTags,
              lat: row.lat ? row.lat : undefined,
              lon: row.lon ? row.lon : undefined,
              report_name: row.report_name,
              company_wide: companyWide
            })
          }

          // check if all reports have exactly 1 subject
          for (const key in reports) {
            const report = reports[key]
            const subjectCount = report.filter((row) => row.type === 'subject').length
            if (subjectCount !== 1) {
              this.errors.push(
                `Property on group_id ${key} has ${subjectCount} subjects. It must have exactly 1 subject`
              )
            }
            // remove reports that have multiple subjects
            if (subjectCount !== 1) {
              delete reports[key]
            }
          }

          this.reports = reports
          this.findProperties().catch(console.error)
        }
      })
    }
  }
}
</script>
